import React from 'react';
import {useControllerProps} from '../../../../../../../domain/controllers/ControllerContext';
import {DestinationPicker} from '../DestinationPicker/DestinationPicker';
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor';
import type {TFunction} from '@wix/wix-i18n-config';
import orderSummaryStyle from '../../OrderSummary.scss';
import classNames from 'clsx';
import {TotalUpdateSkeleton} from '../TotalUpdateSkeleton';
import {DiscountScope, DiscountSource} from '../../../../../../../domain/models/CartSummary/Discount.model';
import {SPECS} from '../../../../../../../domain/specs';

export enum TotalShippingDataHook {
  Root = 'TotalShipping.Root',
  TitleRoot = 'TotalShipping.TitleRoot',
  Title = 'TotalShipping.Title',
  Value = 'TotalShipping.Value',
  Picker = 'ShippingDestination.Picker',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const TotalShipping = () => {
  const {t} = useTranslation();
  const {experiments} = useExperiments();
  const ecomInlineAddressSelectionInCart = experiments.enabled(SPECS.ecomInlineAddressSelectionInCart);
  const {shouldDisplayDeliveryError, isPickup, hasDeliveryAddress, hasShippingSubdivisionError} =
    useControllerProps().orderStore;
  const {cartSummary, isSummaryUpdating, couponAlignmentInCartAndCheckout} = useControllerProps().cartStore;
  const hasDeliveryAddressLegacy =
    (hasDeliveryAddress && !hasShippingSubdivisionError) || ecomInlineAddressSelectionInCart;
  const isFreeShippingCoupon = couponAlignmentInCartAndCheckout
    ? cartSummary?.globalDiscounts.some(
        (discount) => discount.scope === DiscountScope.DELIVERY && discount.sourceType === DiscountSource.COUPON
      )
    : cartSummary?.couponDiscount?.isFreeShippingCoupon;

  const shouldDisplayNoAmount = !cartSummary?.deliverySummary?.method;

  const getShippingRowValueText = (): string | React.ReactNode => {
    if (isSummaryUpdating) {
      return <TotalUpdateSkeleton />;
    }
    if (shouldDisplayNoAmount) {
      return t('cart.summary.shipping.noAmount');
    }
    if (cartSummary.priceSummary.delivery.convertedAmount === 0 || isFreeShippingCoupon) {
      return t('cart.free_shipping');
    }
    return cartSummary.priceSummary.delivery.formattedConvertedAmount;
  };

  const showPrice = !shouldDisplayDeliveryError && hasDeliveryAddressLegacy;
  return (
    <div
      data-hook={TotalShippingDataHook.Root}
      className={ecomInlineAddressSelectionInCart ? orderSummaryStyle.totalShippingRoot : undefined}>
      <dl
        className={
          ecomInlineAddressSelectionInCart
            ? undefined
            : classNames(
                orderSummaryStyle.deliveryLine,
                /* istanbul ignore next */ !hasDeliveryAddressLegacy && orderSummaryStyle.destinationPickerOnly
              )
        }>
        <dt>
          <div data-hook={TotalShippingDataHook.TitleRoot}>
            {hasDeliveryAddressLegacy && (
              <span data-hook={TotalShippingDataHook.Title}>{getTotalShippingTitle({t, isPickup})}</span>
            )}
            {!ecomInlineAddressSelectionInCart && (
              <DestinationPicker
                dataHook={TotalShippingDataHook.Picker}
                hasPartialAddressError={hasShippingSubdivisionError}
              />
            )}
          </div>
        </dt>

        {showPrice && (
          <dd
            data-hook={TotalShippingDataHook.Value}
            data-wix-total-shipping-estimated-shipping={TotalShippingDataHook.Value}>
            {getShippingRowValueText()}
          </dd>
        )}
      </dl>
      {ecomInlineAddressSelectionInCart && (
        <DestinationPicker
          dataHook={TotalShippingDataHook.Picker}
          hasPartialAddressError={hasShippingSubdivisionError}
        />
      )}
    </div>
  );
};

function getTotalShippingTitle({t, isPickup}: {t: TFunction; isPickup: boolean}) {
  return isPickup ? t('cart.estimated_pickup') : t('cart.estimated_shipping');
}
